import { useState } from "react";
import { Share2, Link2, Check } from "lucide-react";
import { toast } from "sonner";

const FacebookIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
);

const XIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

const WhatsAppSvg = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
    </svg>
);

export const SOCIAL_ICONS = { FacebookIcon, XIcon, WhatsAppSvg };

const ShareButtons = ({ title }) => {
    const [copied, setCopied] = useState(false);
    const url = window.location.href;
    const text = `${title} — OM Infra`;

    const share = async (network) => {
        if (navigator.share) {
            try {
                await navigator.share({ title, text, url });
                return;
            } catch { /* user cancelled or unsupported */ }
        }
        const links = {
            whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
        };
        window.open(links[network], "_blank", "noopener,width=600,height=500");
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Could not copy link");
        }
    };

    const btn = "inline-flex items-center gap-2 border border-slate-200 text-slate-700 text-xs font-semibold px-4 py-2.5 rounded-full hover:border-gold hover:text-navy hover:bg-gold-soft/40 transition-all";

    return (
        <div className="flex flex-wrap items-center gap-2.5" data-testid="share-buttons">
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-400 mr-1">
                <Share2 className="w-3.5 h-3.5" /> Share
            </span>
            <button onClick={() => share("whatsapp")} className={btn} data-testid="share-whatsapp">
                <WhatsAppSvg className="w-4 h-4 text-[#25D366]" /> WhatsApp
            </button>
            <button onClick={() => share("facebook")} className={btn} data-testid="share-facebook">
                <FacebookIcon className="w-4 h-4 text-[#1877F2]" /> Facebook
            </button>
            <button onClick={() => share("x")} className={btn} data-testid="share-x">
                <XIcon className="w-4 h-4 text-navy" /> X (Twitter)
            </button>
            <button onClick={copy} className={btn} data-testid="share-copy-link">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Link2 className="w-4 h-4" />}
                {copied ? "Copied" : "Copy Link"}
            </button>
        </div>
    );
};

export default ShareButtons;
